// src/components/inputs/FileUpload/FileUploadZone.tsx
import { Box, Stack, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import UploadImage from "../../../assets/icons/upload.svg";
import {
  MAX_FILE_SIZE,
  SUPPORTED_EXTENSIONS,
  SUPPORTED_MIME_TYPES,
  ZIP_EXTENSION,
  ZIP_MIME_TYPES,
} from "../../../config";
import { useError } from "../../../contexts/ErrorContext";
import { CaseMockResponse } from "../../../onboarding/mock";
import { useFileUpload, usePresignPreview } from "../../../pages/cases/hooks/useCaseManagement";
import { CaseFile } from "../../../pages/cases/types";
import { formatSize } from "../../../utils";
import Button from "../../buttons/Button";
import { FileUpload } from "../types";
import UploadedFileList from "./UploadedFileList";
import ThreeDotLoader from "../../misc/ThreeDotLoader";

const borderAnimation = keyframes`
  0% { 
    background-color: #f6fbf4;
    border-width: 1px; 
    border-color: rgba(0, 0, 0, 0.25); 
  }
  50% { 
    background-color:rgb(139, 214, 109);
    border-width: 2px;
    border-color: rgba(0, 150, 136, 0.75); 
  }
  100% { 
    background-color: rgb(139, 214, 109);
    border-width: 1px;
    border-color: rgba(66, 64, 64, 0.25);
  }
`;
const StyledBox = styled(Box)<{ isDragging: boolean; expanded: boolean }>(
  ({ theme, isDragging, expanded }) => css`
    border: 1px dashed rgba(0, 0, 0, 0.25);
    border-radius: 18px;
    padding: ${theme.spacing(5)};
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin: auto;
    align-self: center;
    cursor: pointer;
    width: ${window.innerWidth * 0.8}px;
    max-width: 860px;
    background-color: #f6fbf4;
    &:hover {
      background-color: #f9f9f9;
    }
    transition: height 0.4s ease-in-out;
    ${isDragging &&
    css`
      animation: ${borderAnimation} 1s;
      border-color: rgba(0, 150, 136, 0.75);
    `}
  `
);

interface FileUploadZoneProps {
  onChange: (files: (CaseFile | FileUpload)[]) => void;
  buttonPosition?: "top" | "bottom";
  title?: string;
  description?: string;
  buttonText?: string;
  forceSmallBUtton?: boolean;
  initialFiles?: CaseFile[];
  value?: (CaseFile | FileUpload)[];
  renderFileList?: (files: (CaseFile | FileUpload)[]) => React.ReactNode;
  customHandleFileChange?: (files: FileList) => void;
  supportOnlyDirectory?: boolean;
  isLoading: boolean;
  supportsDropping?: boolean;
  disabled?: boolean;
  isLoadingFiles?: boolean;
  acceptZip?: boolean;
  panelMode?: "v1" | "v2";
}

const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  onChange,
  renderFileList,
  customHandleFileChange,
  buttonPosition = "top",
  title = "Select a file or drag and drop here",
  description = "Supported formats: PDF, DOCX. File size must not exceed 10MB.",
  buttonText = "Select File",
  forceSmallBUtton = false,
  initialFiles = [],
  value,
  supportOnlyDirectory = false,
  isLoading = true,
  supportsDropping = true,
  disabled = false,
  isLoadingFiles = false,
  acceptZip = false,
  panelMode = "v1",
}) => {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const { files, setFiles } = useFileUpload();
  const [dummyState, setDummyState] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const filesMaxHeight = window.outerHeight * 0.25;
  const { id: caseId } = useParams();
  const { showSuccess, showError } = useError();
  const getPreviewUrl = usePresignPreview(caseId);

  useEffect(() => {
    if (caseId === CaseMockResponse.id) {
      setDummyState((prev) => !prev);
    }
  }, []);

  useEffect(() => {
    if (dummyState || initialFiles.length > 0) {
      setFiles(initialFiles);
      return;
    }
  }, [initialFiles]);

  // Keep internal files in sync with controlled `value` prop (v2 mode).
  // Skip when the reference is already the same to avoid cascading renders on every parent update.
  useEffect(() => {
    if (value !== undefined && value !== files) {
      setFiles(value);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]); // setFiles is a stable setter; files intentionally omitted to avoid loop

  const allowedMimeTypes = [
    ...SUPPORTED_MIME_TYPES,
    ...(acceptZip ? ZIP_MIME_TYPES : []),
  ];
  const allowedExtensions = [
    ...SUPPORTED_EXTENSIONS,
    ...(acceptZip ? [ZIP_EXTENSION.replace(".", "")] : []),
  ];

  const isSupportedType = (file: File) => {
    if (file.type && allowedMimeTypes.includes(file.type)) return true;
    const ext = file.name.split(".").pop()?.toLowerCase();
    return ext ? allowedExtensions.includes(ext) : false;
  };

  const processFiles = useCallback(
    (incomingFiles: FileList | File[]) => {
      const fileArray = Array.from(incomingFiles);
      const unsupported = fileArray.filter((f) => !isSupportedType(f));
      const supported = fileArray.filter((f) => isSupportedType(f));
      const validFiles = supported.filter((f) => f.size <= MAX_FILE_SIZE);
      const oversized = supported.filter((f) => f.size > MAX_FILE_SIZE);

      if (unsupported.length) {
        showError(
          `${unsupported.length} file(s) have an unsupported format. Allowed: ${allowedExtensions
            .map((e) => e.toUpperCase())
            .join(", ")}.`
        );
      }

      if (oversized.length) {
        showError(
          `${oversized.length} file(s) exceed the maximum size of ${formatSize(
            MAX_FILE_SIZE
          )}.`
        );
      }

      const newFileUploads = validFiles.map((file) => ({
        url: URL.createObjectURL(file),
        file,
      }));

      setFiles((prev) => {
        const incomingNames = new Set(newFileUploads.map((f) => f.file.name));
        const dedupedPrev = prev.filter((f) =>
          "file" in f ? !incomingNames.has(f.file.name) : true
        );
        const next = [...dedupedPrev, ...newFileUploads];
        onChange(next);
        return next;
      });
    },
    [files, onChange, setFiles, showError, acceptZip]
  );

  const handleFileUpload = useCallback(() => {
    if (disabled || isLoading || isLoadingFiles) {
      return;
    }
    fileInputRef.current?.click();
  }, [fileInputRef, disabled, isLoading, isLoadingFiles]);

  const handleFileChange = useCallback(() => {
    const filesLoaded = fileInputRef.current?.files;
    if (!filesLoaded) {
      return;
    }
    if (customHandleFileChange) {
      customHandleFileChange(filesLoaded);
      return;
    }

    processFiles(filesLoaded);
  }, [fileInputRef, processFiles, customHandleFileChange]);

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!supportsDropping || isLoading || disabled || isLoadingFiles) {
      return;
    }
    setIsDragging(false);
    const filesLoaded = event.dataTransfer.files;
    if (customHandleFileChange) {
      customHandleFileChange(filesLoaded);
      return;
    }

    processFiles(filesLoaded);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!supportsDropping || isLoading || disabled || isLoadingFiles) {
      return;
    }
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!supportsDropping || isLoading || disabled || isLoadingFiles) {
      return;
    }
    setIsDragging(false);
  };

  const UploadButton: React.FC = () => (
    <StyledBox
      expanded={files.length > 0}
      p={files.length > 0 ? 2 : 3}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      isDragging={isDragging}
    >
      <input
        ref={(ref) => {
          if (ref) {
            if (supportOnlyDirectory) {
              console.info("Setting directory upload attributes");
              ref.setAttribute("webkitdirectory", "true");
              ref.setAttribute("directory", "true");
            }
          }
          fileInputRef.current = ref;
        }}
        accept={supportOnlyDirectory ? "" : [
          ...SUPPORTED_MIME_TYPES,
          ...SUPPORTED_EXTENSIONS.map((ext) => `.${ext}`),
          ...(acceptZip ? [...ZIP_MIME_TYPES, ZIP_EXTENSION] : []),
        ].join(",")}
        multiple={supportOnlyDirectory ? false : true}
        type="file"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
      <Box onClick={handleFileUpload} width={"100%"}>
        <Stack
          direction={"row"}
          justifyContent="space-between"
          width={"100%"}
          alignItems="center"
          spacing={2}
        >
          <img src={UploadImage} alt="Upload" />
          <Box width={"100%"}>
            <Typography fontWeight="medium" color="success" variant="body2">
              {title}
            </Typography>
            <Typography
              variant={"caption"}
              fontWeight="light"
              color="textSecondary"
            >
              {description}
            </Typography>
          </Box>
          <Button
            disabled={isLoading || disabled || isLoadingFiles}
            sx={{ px: 0, backgroundColor: "#D0E8D7", color: "#0B1F15" }}
            width={280}
            variant={"filled"}
            size="small"
            onClick={() => {}}
          >
            {buttonText}
          </Button>
        </Stack>
      </Box>
    </StyledBox>
  );

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      {buttonPosition === "top" && <UploadButton />}
      {isLoadingFiles && (
        <Box
          sx={{
            width: "100%",
          }}
        >
          <Typography
            variant="body2"
            fontWeight="light"
            color="textSecondary"
            textAlign="center"
          >
            <ThreeDotLoader label="Loading files" />
          </Typography>
        </Box>
      )}
      {(panelMode === "v2" || files.length > 0) &&
        (renderFileList ? (
          renderFileList(files)
        ) : (
          <UploadedFileList
            disabled={disabled}
            getPreviewUrl={caseId ? getPreviewUrl : undefined}
            files={files}
            onDelete={(file) => {
              const updated = files.filter((f) => f !== file);
              console.info("Updated files after deletion:", updated, files.length, updated.length);
              setFiles(updated);
              onChange(updated);
            }}
          />
        ))}
      {buttonPosition === "bottom" && <UploadButton />}
    </Stack>
  );
};

export default FileUploadZone;
